import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

interface SearchResult {
  id?: string;
  title: string;
  type: string;
  snippet: string;
}

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!q) {
      setResults([]);
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('jwt_token');
        const res = await fetch(`${API_URL}/search?q=${encodeURIComponent(q)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) {
          throw new Error('Failed to fetch search results');
        }
        const data = await res.json();
        setResults(data.data || data || []);
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [q]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Search Results for "{q}"</h1>
      
      {loading && <p className="text-slate-500">Searching...</p>}
      {error && <p className="text-red-500">{error}</p>}
      
      {!loading && !error && results.length === 0 && q && (
        <p className="text-slate-500">No results found.</p>
      )}

      {!loading && !error && results.length > 0 && (
        <div className="space-y-4">
          {results.map((result, i) => (
            <div key={result.id || i} className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm hover:border-slate-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-medium text-slate-900">{result.title}</h2>
                <span className="text-xs font-semibold px-2 py-1 bg-slate-100 text-slate-600 rounded-full uppercase tracking-wide">
                  {result.type}
                </span>
              </div>
              <p 
                className="text-slate-600 text-sm [&>b]:text-slate-900 [&>b]:font-semibold"
                dangerouslySetInnerHTML={{ __html: result.snippet }} 
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
